/**
 * Batch actuals — what the brewer measured, next to what the recipe predicted.
 *
 * ```ts
 * const actuals = computeBatchActuals(batch);
 * actuals.brewhouseEfficiencyPercent;   // 65.12 — from OG and fermenter volume
 * actuals.deltas;                       // the predicted-vs-actual table, row by row
 * ```
 *
 * Everything here is **derived**: nothing is stored, so a fixed formula in core
 * or a corrected reading in the batch immediately shows up, and there is no
 * second copy of a number to drift. It is also **pure** — no clock, no IO, in
 * keeping with the rest of this package.
 *
 * ## Where the numbers come from
 *
 * The prediction side is `calculateRecipe` run over the batch's **frozen
 * snapshots**, never over the live recipe. That is the entire point of the
 * snapshot: editing the source recipe next week must not move last week's
 * batch. `bridge/actuals.test.ts` asserts exactly that.
 *
 * The measured side is core's back-computation, one function per figure:
 *
 * | Figure | Core function | Needs |
 * | --- | --- | --- |
 * | mash efficiency | `mashEfficiencyFromMeasurement` | post-boil (or pre-boil) SG **and** volume |
 * | brewhouse efficiency | `brewhouseEfficiencyFromMeasurement` | OG **and** fermenter volume |
 * | ABV, both formulas | `abvSimple` / `abvAdvanced` | OG **and** FG |
 * | apparent attenuation | `apparentAttenuationPercent` | OG **and** FG |
 * | boil-off rate | arithmetic, below | pre- and post-boil volume, non-zero boil |
 *
 * Every one of them returns `null` when its inputs are not all present. A
 * missing measurement is a missing measurement — this module never substitutes
 * the prediction for it, because a table whose "actual" column silently repeats
 * the "predicted" column is worse than an empty cell.
 *
 * ## The one assumption worth stating
 *
 * **Boil-off** is computed as `(preBoilVolumeL − postBoilVolumeL) / hours`,
 * treating both readings as taken on the same thermometer — i.e. both hot, off
 * the same sight glass. That is what a brewer actually does. If the post-boil
 * figure has been corrected to 20 °C and the pre-boil one has not, the
 * difference absorbs ~4 % thermal contraction and the rate reads about 0.8 L/h
 * high on a 20 L batch. Stated rather than corrected for, because guessing which
 * of the two readings the brewer corrected would be worse than being explicit
 * about the convention.
 */

import {
  abvAdvanced,
  abvSimple,
  apparentAttenuationPercent,
  brewhouseEfficiencyFromMeasurement,
  calculateRecipe,
  fermentableEfficiencyFraction,
  mashEfficiencyFromMeasurement,
  sgToGravityPoints,
} from '@beerwright/core';
import type { BatchDoc } from '../document/batch';
import type { EquipmentProfileDoc } from '../document/equipment';
import type { WorkspaceState } from '../document/workspace';
import { toCalculationInputs, toFermentableAddition } from './calculation';

// ---------------------------------------------------------------------------
// The delta table
// ---------------------------------------------------------------------------

/** Which vital a {@link BatchDelta} row is about. */
export type BatchVitalKey =
  | 'preBoilGravity'
  | 'postBoilGravity'
  | 'og'
  | 'fg'
  | 'abv'
  | 'preBoilVolumeL'
  | 'postBoilVolumeL'
  | 'fermenterVolumeL'
  | 'packagedVolumeL'
  | 'mashEfficiencyPercent'
  | 'brewhouseEfficiencyPercent'
  | 'apparentAttenuationPercent';

/** How a delta row should be rendered. Canonical units — display conversion is the UI's job. */
export type BatchDeltaUnit = 'sg' | 'litres' | 'percent';

/** One row of the predicted-vs-actual table. */
export interface BatchDelta {
  /** Which vital. */
  readonly key: BatchVitalKey;
  /** English label, e.g. `Original gravity`. */
  readonly label: string;
  /** Canonical unit, for the formatter. */
  readonly unit: BatchDeltaUnit;
  /** What the frozen snapshot predicted. */
  readonly predicted: number;
  /** What the brewer measured or what core back-computed, or `null`. */
  readonly actual: number | null;
  /** `actual − predicted`, or `null` when there is no actual. */
  readonly delta: number | null;
}

/** How the measured attenuation sits against the snapshot yeast's published band. */
export interface AttenuationVsYeast {
  /** Measured apparent attenuation, %. */
  readonly actualPercent: number;
  /** Lowest published minimum across the batch's yeasts, %. */
  readonly minPercent: number;
  /** Highest published maximum across the batch's yeasts, %. */
  readonly maxPercent: number;
  /** Whether the measurement lands inside the band. */
  readonly withinBand: boolean;
}

/** Packaged volume against what the waterfall predicted. */
export interface PackagedYield {
  /** Predicted packaged volume, litres. */
  readonly predictedL: number;
  /** Measured packaged volume, litres. */
  readonly actualL: number;
  /** `actual − predicted`, litres. */
  readonly deltaL: number;
  /** The same delta as a % of the prediction. */
  readonly deltaPercent: number;
}

/**
 * The three facts that explain any mash-vs-brewhouse efficiency pair.
 *
 * A brewer looking at these two rows reasonably expects brewhouse to sit below
 * mash, and is right to be suspicious when it does not — but `brewhouse > mash`
 * is **not** impossible, so this is deliberately not a warning. It is the
 * material a caller needs to say *why* a particular pair sits the way it does.
 *
 * Two things separate the figures, and both are reported here:
 *
 * - **Wort left in the kettle.** The only difference the model intends. Core is
 *   explicit that losses between kettle and fermenter "remove wort, not
 *   concentration", so with `recordedKettleLossL` at zero the two efficiencies
 *   are one quantity computed from two readings of the same wort — and any
 *   disagreement between those readings shows up as a spurious gap.
 * - **Extract that skips the mash.** Sugar and extract dissolve at ~100 %,
 *   which lifts the all-inclusive brewhouse denominator's numerator without
 *   touching the mashable-only mash figure. On such a grist brewhouse can
 *   legitimately outrun mash however carefully the volumes were measured.
 *
 * `gravityGapPoints` is the residual: what is left once the volumes agree.
 */
export interface EfficiencyRelation {
  /**
   * Post-boil volume minus fermenter volume, **litres**, or `null` when either
   * is missing. Zero means nothing was recorded as staying in the kettle, which
   * is legitimate for a brewer who dumps the lot — and is also what collapses
   * the two efficiencies into one number.
   */
  readonly recordedKettleLossL: number | null;
  /** Whether the grist contains anything that dissolves at a fixed efficiency. */
  readonly hasFixedEfficiencyAdditions: boolean;
  /**
   * OG minus post-boil gravity, in **gravity points**, or `null` when either is
   * missing. The model says these are the same wort, so anything non-zero is
   * the two instruments disagreeing.
   */
  readonly gravityGapPoints: number | null;
}

/** Everything derivable from one batch. */
export interface BatchActuals {
  /** Back-computed mash efficiency, %, or `null`. `NaN` from core (all-extract) becomes `null`. */
  readonly mashEfficiencyPercent: number | null;
  /** Which sample the mash efficiency came from. */
  readonly mashEfficiencyStage: 'pre-boil' | 'post-boil' | null;
  /** Back-computed brewhouse efficiency, %, or `null`. */
  readonly brewhouseEfficiencyPercent: number | null;
  /** ABV by the simple (OG − FG) × 131.25 formula, %, or `null`. */
  readonly abvSimplePercent: number | null;
  /** ABV by core's default (Cutaia) formula, %, or `null`. */
  readonly abvAdvancedPercent: number | null;
  /** Measured apparent attenuation, %, or `null`. */
  readonly apparentAttenuationPercent: number | null;
  /** The attenuation against the snapshot yeast's band, or `null`. */
  readonly attenuationVsYeast: AttenuationVsYeast | null;
  /** Packaged volume against the prediction, or `null`. */
  readonly packagedYield: PackagedYield | null;
  /** Measured evaporation rate, litres per hour, or `null`. See the module header. */
  readonly boilOffRateLPerHour: number | null;
  /** Why the two efficiencies sit as they do, or `null` when nothing was measured. */
  readonly efficiencyRelation: EfficiencyRelation | null;
  /** The predicted-vs-actual table, in display order. Always complete. */
  readonly deltas: readonly BatchDelta[];
  /** True when at least one figure on the measured side exists. */
  readonly hasMeasurements: boolean;
}

/** Labels for the delta rows. Kept beside the key union so neither can drift. */
const DELTA_LABELS: Readonly<Record<BatchVitalKey, string>> = {
  preBoilGravity: 'Pre-boil gravity',
  postBoilGravity: 'Post-boil gravity',
  og: 'Original gravity',
  fg: 'Final gravity',
  abv: 'ABV',
  preBoilVolumeL: 'Pre-boil volume',
  postBoilVolumeL: 'Post-boil volume',
  fermenterVolumeL: 'Into fermenter',
  packagedVolumeL: 'Packaged',
  mashEfficiencyPercent: 'Mash efficiency',
  brewhouseEfficiencyPercent: 'Brewhouse efficiency',
  apparentAttenuationPercent: 'Apparent attenuation',
};

/** `NaN` (core's "mash efficiency does not apply") and `undefined` both become `null`. */
function finiteOrNull(value: number | undefined): number | null {
  return value === undefined || !Number.isFinite(value) ? null : value;
}

/** One row, with the subtraction done only when both halves exist. */
function delta(
  key: BatchVitalKey,
  unit: BatchDeltaUnit,
  predicted: number,
  actual: number | null,
): BatchDelta {
  return {
    key,
    label: DELTA_LABELS[key],
    unit,
    predicted,
    actual,
    delta: actual === null ? null : actual - predicted,
  };
}

/**
 * Everything a batch's measurements support, computed against its frozen
 * snapshots.
 *
 * @param batch - The batch. Only `recipeSnapshot`, `equipmentSnapshot` and
 *   `measurements` are read; status and names are irrelevant to the maths.
 */
export function computeBatchActuals(batch: BatchDoc): BatchActuals {
  const { recipe, equipment } = toCalculationInputs(batch.recipeSnapshot, batch.equipmentSnapshot);
  const predicted = calculateRecipe(recipe, equipment);
  const m = batch.measurements;
  const fermentables = batch.recipeSnapshot.fermentables.map(toFermentableAddition);

  // --- efficiencies --------------------------------------------------------
  // Post-boil is preferred: it is the sample that has seen the whole grist and
  // the whole sparge, so it is the honest reading of what the mash gave up. A
  // pre-boil sample is accepted as a fallback because plenty of brewers take
  // one and nothing else before the kettle.
  const mashSample: { stage: 'pre-boil' | 'post-boil'; sg: number; volumeL: number } | null =
    m.postBoil.sg !== null && m.postBoil.volumeL !== null
      ? { stage: 'post-boil', sg: m.postBoil.sg, volumeL: m.postBoil.volumeL }
      : m.preBoil.sg !== null && m.preBoil.volumeL !== null
        ? { stage: 'pre-boil', sg: m.preBoil.sg, volumeL: m.preBoil.volumeL }
        : null;

  const mashEfficiencyPercent =
    mashSample === null
      ? null
      : finiteOrNull(
          mashEfficiencyFromMeasurement({
            fermentables,
            measuredGravity: mashSample.sg,
            measuredVolumeL: mashSample.volumeL,
            stage: mashSample.stage,
          }),
        );

  const brewhouseEfficiencyPercent =
    m.originalGravity === null || m.fermenterVolumeL === null
      ? null
      : finiteOrNull(
          brewhouseEfficiencyFromMeasurement({
            fermentables,
            measuredOg: m.originalGravity,
            volumeIntoFermenterL: m.fermenterVolumeL,
          }),
        );

  // --- alcohol and attenuation --------------------------------------------
  const bothGravities = m.originalGravity !== null && m.finalGravity !== null;
  const og = m.originalGravity;
  const fg = m.finalGravity;
  const abvSimplePercent = bothGravities && og !== null && fg !== null ? abvSimple(og, fg) : null;
  const abvAdvancedPercent =
    bothGravities && og !== null && fg !== null ? abvAdvanced(og, fg) : null;
  const attenuation =
    bothGravities && og !== null && fg !== null ? apparentAttenuationPercent(og, fg) : null;

  const yeasts = batch.recipeSnapshot.yeasts;
  const attenuationVsYeast: AttenuationVsYeast | null =
    attenuation === null || yeasts.length === 0
      ? null
      : (() => {
          const minPercent = Math.min(...yeasts.map((y) => y.attenuationMinPercent));
          const maxPercent = Math.max(...yeasts.map((y) => y.attenuationMaxPercent));
          return {
            actualPercent: attenuation,
            minPercent,
            maxPercent,
            withinBand: attenuation >= minPercent && attenuation <= maxPercent,
          };
        })();

  // --- yield and boil-off --------------------------------------------------
  const packagedYield: PackagedYield | null =
    m.packagedVolumeL === null || predicted.volumes.packagedVolumeL <= 0
      ? null
      : {
          predictedL: predicted.volumes.packagedVolumeL,
          actualL: m.packagedVolumeL,
          deltaL: m.packagedVolumeL - predicted.volumes.packagedVolumeL,
          deltaPercent:
            ((m.packagedVolumeL - predicted.volumes.packagedVolumeL) /
              predicted.volumes.packagedVolumeL) *
            100,
        };

  const boilHours = batch.recipeSnapshot.boilTimeMinutes / 60;
  const boilOffRateLPerHour =
    m.preBoil.volumeL === null || m.postBoil.volumeL === null || boilHours <= 0
      ? null
      : (m.preBoil.volumeL - m.postBoil.volumeL) / boilHours;

  // --- why the two efficiencies sit as they do -----------------------------
  // "Fixed efficiency" is decided the way core itself decides it: run the
  // helper at two mash efficiencies and see whether the answer moves. Sugar,
  // extract and lactose do not move; grain does.
  const hasFixedEfficiencyAdditions = fermentables.some(
    (f) => fermentableEfficiencyFraction(f, 100) === fermentableEfficiencyFraction(f, 50),
  );

  // --- the table -----------------------------------------------------------
  const deltas: readonly BatchDelta[] = [
    delta('preBoilGravity', 'sg', predicted.vitals.preBoilGravity, m.preBoil.sg),
    delta('postBoilGravity', 'sg', predicted.vitals.postBoilGravity, m.postBoil.sg),
    delta('og', 'sg', predicted.vitals.og, m.originalGravity),
    delta('fg', 'sg', predicted.vitals.fg, m.finalGravity),
    delta('abv', 'percent', predicted.vitals.abv, abvAdvancedPercent),
    delta('preBoilVolumeL', 'litres', predicted.volumes.preBoilVolumeL, m.preBoil.volumeL),
    delta('postBoilVolumeL', 'litres', predicted.volumes.postBoilVolumeL, m.postBoil.volumeL),
    delta('fermenterVolumeL', 'litres', predicted.volumes.volumeIntoFermenterL, m.fermenterVolumeL),
    delta('packagedVolumeL', 'litres', predicted.volumes.packagedVolumeL, m.packagedVolumeL),
    delta(
      'mashEfficiencyPercent',
      'percent',
      predicted.vitals.mashEfficiencyPercent,
      mashEfficiencyPercent,
    ),
    delta(
      'brewhouseEfficiencyPercent',
      'percent',
      predicted.vitals.brewhouseEfficiencyPercent,
      brewhouseEfficiencyPercent,
    ),
    delta(
      'apparentAttenuationPercent',
      'percent',
      predicted.vitals.apparentAttenuationPercent,
      attenuation,
    ),
  ];

  const hasMeasurements = deltas.some((row) => row.actual !== null);
  const efficiencyRelation: EfficiencyRelation | null = !hasMeasurements
    ? null
    : {
        recordedKettleLossL:
          m.postBoil.volumeL === null || m.fermenterVolumeL === null
            ? null
            : m.postBoil.volumeL - m.fermenterVolumeL,
        hasFixedEfficiencyAdditions,
        gravityGapPoints:
          m.postBoil.sg === null || m.originalGravity === null
            ? null
            : sgToGravityPoints(m.originalGravity) - sgToGravityPoints(m.postBoil.sg),
      };

  return {
    mashEfficiencyPercent,
    mashEfficiencyStage: mashSample?.stage ?? null,
    brewhouseEfficiencyPercent,
    abvSimplePercent,
    abvAdvancedPercent,
    apparentAttenuationPercent: attenuation,
    attenuationVsYeast,
    packagedYield,
    boilOffRateLPerHour,
    efficiencyRelation,
    deltas,
    hasMeasurements,
  };
}

// ---------------------------------------------------------------------------
// Equipment feedback
// ---------------------------------------------------------------------------

/** One aggregated figure, with the profile's current value beside it. */
export interface FeedbackFigure {
  /** Median of the measured values across the sampled batches. See {@link medianOrNull}. */
  readonly measuredMedian: number;
  /** How many batches contributed. */
  readonly sampleCount: number;
  /** What the profile currently says. */
  readonly profileValue: number;
  /** `measuredMedian − profileValue`, in the figure's own unit. */
  readonly delta: number;
}

/**
 * Advisory feedback for one equipment profile, aggregated from its batches.
 *
 * **Never auto-applied.** This is data for a card that says "your measured
 * brewhouse efficiency runs at 65.6 % against the profile's 70 % — update the
 * profile?", and the update is one explicit, undoable `equipment.update` the
 * user chooses. Silently recalibrating a brewer's system behind their back
 * would move every recipe's predicted OG at once.
 *
 * The aggregate is the **median**, not the mean — see {@link medianOrNull} for
 * why. Both the batch page's card and the equipment page's calibration rows
 * read these figures, so the two surfaces cannot suggest different numbers.
 */
export interface EquipmentFeedback {
  /** The profile the feedback is about. */
  readonly profileId: string;
  /** Batches on this profile that contributed at least one figure. */
  readonly batchCount: number;
  /** Their ids, in workspace-key order — stable, and useful for a "which batches?" affordance. */
  readonly batchIds: readonly string[];
  /** Median measured mash efficiency, or `null` when no batch supports one. */
  readonly mashEfficiencyPercent: FeedbackFigure | null;
  /** Median measured brewhouse efficiency, or `null`. */
  readonly brewhouseEfficiencyPercent: FeedbackFigure | null;
  /** Median measured evaporation rate, litres per hour, or `null`. */
  readonly boilOffRateLPerHour: FeedbackFigure | null;
}

/**
 * Median of a list, or `null` when it is empty. An even count takes the mean of
 * the middle two, which is the ordinary definition and keeps the figure moving
 * continuously as batches accumulate.
 *
 * ## Why the median and not the mean
 *
 * Brew days go wrong in one direction and badly. A stuck mash, a scorched boil,
 * a dip tube that sucked air, a dipstick read in the dark — any one of them
 * produces a single measurement tens of percentage points away from the truth,
 * and a mean carries that straight into the suggestion the brewer is offered.
 * Four honest brews near 70 % and one disaster at 20 % average to 60 %; their
 * median is still 70 %. Since the whole point of this figure is *"what does
 * this rig usually do"*, the estimator has to be the one that ignores the brew
 * day that was not usual. `actuals.test.ts` pins exactly that case.
 *
 * The switch from the mean was safe to make because the figure is **advisory
 * and terminal**: it is rendered, and it is offered as a value the brewer may
 * choose to write into their profile. Nothing downstream computes from it — no
 * prediction, no stored document, no other derived number reads a
 * `FeedbackFigure` — so changing the estimator changes a suggestion and nothing
 * else. A profile the brewer already updated keeps whatever number they applied
 * until they apply another.
 *
 * Core's kettle-pH buffering uses the same median, with the mean of the middle
 * two on an even count. Keep the two definitions in step.
 */
function medianOrNull(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length >> 1;
  const upper = sorted[middle];
  const lower = sorted[sorted.length % 2 === 1 ? middle : middle - 1];
  // Both indices are in range for a non-empty list; the guards are for the
  // compiler's benefit under `noUncheckedIndexedAccess`.
  if (upper === undefined || lower === undefined) return null;
  return (lower + upper) / 2;
}

/** Build a figure, or `null` when nothing was measured. */
function figure(values: readonly number[], profileValue: number): FeedbackFigure | null {
  const measuredMedian = medianOrNull(values);
  if (measuredMedian === null) return null;
  return {
    measuredMedian,
    sampleCount: values.length,
    profileValue,
    delta: measuredMedian - profileValue,
  };
}

/**
 * Aggregate a workspace's batches into advisory feedback for one profile.
 *
 * Each of the three figures is the **median** of the batches that supported it,
 * so a single stuck mash or scorched boil cannot drag the suggestion — see
 * {@link medianOrNull}.
 *
 * The three figures are counted **independently**: a batch that recorded an OG
 * and a fermenter volume but no kettle volumes contributes to the brewhouse
 * figure and not to the boil-off one, which is why each `FeedbackFigure` carries
 * its own `sampleCount` and why that count can be smaller than `batchCount`.
 *
 * Batches are matched on `equipmentSnapshot.id`, not on their recipe's current
 * `equipmentProfileId`: the snapshot records the rig the beer was actually
 * brewed on, which is the only defensible basis for "recalibrate this rig".
 *
 * `archived` batches are excluded — filing a batch away is the brewer saying it
 * should stop informing anything.
 *
 * @param state - The workspace.
 * @param profileId - Which equipment profile to report on.
 * @returns The feedback. Zero-batch and zero-measurement cases come back with
 *   `batchCount: 0` and three `null` figures rather than as an absence, so the
 *   caller can render "not enough data yet" without a second code path.
 */
export function equipmentFeedback(state: WorkspaceState, profileId: string): EquipmentFeedback {
  const profile: EquipmentProfileDoc | undefined = state.equipmentProfiles[profileId];
  const batchIds: string[] = [];
  const mash: number[] = [];
  const brewhouse: number[] = [];
  const boilOff: number[] = [];

  for (const batch of Object.values(state.batches)) {
    if (batch.equipmentSnapshot.id !== profileId) continue;
    if (batch.status === 'archived') continue;
    const actuals = computeBatchActuals(batch);
    let contributed = false;
    if (actuals.mashEfficiencyPercent !== null) {
      mash.push(actuals.mashEfficiencyPercent);
      contributed = true;
    }
    if (actuals.brewhouseEfficiencyPercent !== null) {
      brewhouse.push(actuals.brewhouseEfficiencyPercent);
      contributed = true;
    }
    if (actuals.boilOffRateLPerHour !== null) {
      boilOff.push(actuals.boilOffRateLPerHour);
      contributed = true;
    }
    if (contributed) batchIds.push(batch.id);
  }

  return {
    profileId,
    batchCount: batchIds.length,
    batchIds,
    mashEfficiencyPercent: figure(mash, profile?.mashEfficiencyPercent ?? 0),
    brewhouseEfficiencyPercent: figure(brewhouse, profile?.brewhouseEfficiencyPercent ?? 0),
    boilOffRateLPerHour: figure(boilOff, profile?.boilOffRateLPerHour ?? 0),
  };
}
